/**
 * API Key Setup Card
 *
 * Guides the user through configuring their Hydrawise API key.
 */

import React, { useState } from 'react';
import { useHydrawiseStore } from '../hydrawise-store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Key, ExternalLink } from 'lucide-react';

export const ApiKeySetupCard: React.FC = () => {
  const { saveApiKey, isLoading } = useHydrawiseStore();
  const [apiKey, setApiKey] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setLocalError('Please enter your API key.');
      return;
    }
    setLocalError('');
    try {
      await saveApiKey(apiKey.trim());
    } catch {
      setLocalError('Invalid API key. Please check and try again.');
    }
  };

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Connect Hydrawise
        </CardTitle>
        <CardDescription>
          Enter your Hunter Hydrawise API key to monitor and control your irrigation system.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-1">
          <p>To generate an API key:</p>
          <ol className="list-decimal list-inside space-y-1 ml-1">
            <li>Sign in at app.hydrawise.com</li>
            <li>Click the menu icon, then Account Details</li>
            <li>Scroll to Account Settings</li>
            <li>Click "Generate API Key"</li>
          </ol>
        </div>

        <a
          href="https://app.hydrawise.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline"
        >
          Open Hydrawise Portal
          <ExternalLink className="h-3 w-3" />
        </a>

        <div className="space-y-2">
          <Label htmlFor="hydrawise-api-key">API Key</Label>
          <Input
            id="hydrawise-api-key"
            type="password"
            placeholder="Paste your Hydrawise API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
        </div>

        {localError && (
          <p className="text-sm text-destructive">{localError}</p>
        )}

        <Button onClick={handleSave} disabled={isLoading || !apiKey.trim()}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
              Connecting...
            </>
          ) : (
            'Connect'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
